import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Settings2 } from 'lucide-react'
import AccountSettings from '@/components/settings/AccountSettings'
import DataSettings from '@/components/settings/DataSettings'
import BillingSettings from '@/components/settings/BillingSettings'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from '@/hooks/useTranslation'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import { isAccountOwner } from '@/lib/programPermissions'

const Settings = () => {
	const { t } = useTranslation()
	const { role } = useTeamContext()
	const { isInvitedProgram } = useProgramContext()
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const tabParam = searchParams.get('tab')
	
	const canAccessBilling = isAccountOwner(role) && !isInvitedProgram

	// Set default tab based on URL parameter
	const validTabs = ['account', 'programs', 'billing']
	const defaultTab = validTabs.includes(tabParam || '') ? tabParam : 'account'

	// Redirect notifications tab to dedicated page
	useEffect(() => {
		if (tabParam === 'notifications') {
			navigate('/notification-settings', { replace: true })
		}
	}, [tabParam, navigate])

	// Handle success or cancelled parameters for billing
	useEffect(() => {
		const success = searchParams.get('success')
		const cancelled = searchParams.get('cancelled')

		if (success === 'true') {
			// Handle successful payment
			// You could show a toast notification here
		} else if (cancelled === 'true') {
			// Handle cancelled payment
			// You could show a toast notification here
		}
	}, [searchParams])

	// Handle tab change
	const handleTabChange = (value: string) => {
		navigate(`/settings?tab=${value}`)
	}

	return (
		<div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50 mb-6">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Settings2 className="h-5 w-5 text-primary" />
					</div>
					<div>
						<h1 className="text-xl md:text-2xl font-semibold text-foreground">
							{t('settings.title')}
						</h1>
						<p className="text-sm text-muted-foreground mt-0.5">
							{t('settings.description')}
						</p>
					</div>
				</div>
			</motion.div>

			<Tabs
				defaultValue={defaultTab}
				className="w-full"
				onValueChange={handleTabChange}>
				<TabsList>
					<TabsTrigger value="account">{t('settings.accountTab')}</TabsTrigger>
					{canAccessBilling && (
						<TabsTrigger value="billing">{t('settings.billingTab')}</TabsTrigger>
					)}
				</TabsList>

				<TabsContent value="account">
					<Card>
						<CardHeader>
							<CardTitle>{t('settings.accountSettings')}</CardTitle>
						</CardHeader>
						<CardContent>
							<AccountSettings />
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="data">
					<Card>
						<CardHeader>
							<CardTitle>{t('settings.dataPrivacySettings')}</CardTitle>
						</CardHeader>
						<CardContent>
							<DataSettings />
						</CardContent>
					</Card>
				</TabsContent>

				{canAccessBilling && (
					<TabsContent value="billing">
						<BillingSettings />
					</TabsContent>
				)}
			</Tabs>
		</div>
	)
}

export default Settings
